import { useEffect, useMemo } from 'react';
import useText from '@/hooks/useText';
import { useSearchTabNavigation } from '@/hooks/search/useSearchTabNavigation';
import { useRecentSearchesStore } from '@/store/recentSearchesStore';
import { useSearchTabsStore } from '@/store/searchTabsStore';
import { entriesFromSearchTabs } from '@/utils/recentSearches';
import { trackEvent } from '@/utils/analytics';

function seedWhenReady(
  seedFromSearchTabs: (tabs: Array<{ title: string; search: string; updatedAt: number }>) => void,
) {
  const recentReady = useRecentSearchesStore.persist.hasHydrated();
  const tabsReady = useSearchTabsStore.persist.hasHydrated();
  if (!recentReady || !tabsReady) return;
  seedFromSearchTabs(useSearchTabsStore.getState().tabs);
}

function RecentSearches() {
  const label = useText('home.hero.recent_searches');
  const recentItems = useRecentSearchesStore(s => s.items);
  const seedFromSearchTabs = useRecentSearchesStore(s => s.seedFromSearchTabs);
  const tabs = useSearchTabsStore(s => s.tabs);
  const { navigateSearch } = useSearchTabNavigation();

  const items = useMemo(
    () => (recentItems.length > 0 ? recentItems : entriesFromSearchTabs(tabs)),
    [recentItems, tabs],
  );

  useEffect(() => {
    seedWhenReady(seedFromSearchTabs);
    const unsubRecent = useRecentSearchesStore.persist.onFinishHydration(() => {
      seedWhenReady(seedFromSearchTabs);
    });
    const unsubTabs = useSearchTabsStore.persist.onFinishHydration(() => {
      seedWhenReady(seedFromSearchTabs);
    });
    return () => {
      unsubRecent();
      unsubTabs();
    };
  }, [seedFromSearchTabs]);

  if (items.length === 0) return null;

  return (
    <div className="home-recent-searches" aria-label={label}>
      <span className="home-recent-searches__label">{label}</span>
      <ul className="home-recent-searches__list">
        {items.map(item => (
          <li key={item.identity}>
            <button
              type="button"
              className="home-recent-searches__chip"
              title={item.title}
              onClick={() => {
                trackEvent({
                  name: 'navigation_click',
                  buttonId: 'home_recent_search',
                  label: item.title,
                  page: 'home',
                  section: 'hero',
                  feature: 'search',
                  eventCategory: 'navigation',
                });
                navigateSearch({ search: item.search, title: item.title });
              }}
            >
              {item.title}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default RecentSearches;
